package net.bothosting.docs.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import net.bothosting.docs.toSitePalette
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span

private val SIDEBAR_WIDTH = SidebarTheme.sidebarWidth

@Composable
fun Sidebar() {
    val context = rememberPageContext()
    val sitePalette = ColorMode.current.toSitePalette()
    val textColor = ColorMode.current.toPalette().color

    Box(
        Modifier
            .width(SIDEBAR_WIDTH)
            .minWidth(SIDEBAR_WIDTH)
            .flexShrink(0)
            .fillMaxHeight()
            .backgroundColor(sitePalette.sidebarBackground)
            .borderRight(1.px, LineStyle.Solid, Color.argb(0x661a1f2e))
            .overflow(Overflow.Auto)
            .classNames("sidebar")
    ) {
        Column(
            Modifier
                .fillMaxWidth()
        ) {
            // ── Navigation items ────────────────────────────────────────
            Column(
                Modifier
                    .fillMaxWidth()
                    .padding(
                        top = SidebarTheme.navPaddingTop,
                        bottom = SidebarTheme.navPaddingBottom,
                        leftRight = SidebarTheme.navPaddingLeftRight
                    )
                    .gap(SidebarTheme.navItemGap)
            ) {
                // Doc sections from generated nav structure
                navStructure.forEach { section ->
                    SectionLabel(section.title)

                    section.items.forEach { item ->
                        val isActive = context.route.path == item.path
                        NavItemLink(
                            label = item.title,
                            path = item.path,
                            icon = item.icon,
                            isActive = isActive,
                        )
                    }

                    Box(
                        Modifier
                            .fillMaxWidth()
                            .height(1.px)
                            .backgroundColor(Color.argb(0x1a1a1f2e))
                            .margin(topBottom = SidebarTheme.separatorMarginY)
                    )
                }

                // ── External resources section ──────────────────────────
                SectionLabel("Resources")

                NavExtLink(
                    href = "https://wiki.bot-hosting.net",
                    label = "Knowledge Base",
                    icon = "fa-book-open",
                )
                NavExtLink(
                    href = "https://discord.com/invite/6FKKj4qEM6",
                    label = "Discord",
                    icon = "fa-brands fa-discord",
                )
                NavExtLink(
                    href = "https://advertise.bot-hosting.net",
                    label = "Advertise",
                    icon = "fa-bullhorn",
                )
            }
        }
    }
}

@Composable
private fun SectionLabel(label: String) {
    val sitePalette = ColorMode.current.toSitePalette()
    SpanText(
        label,
        Modifier
            .fillMaxWidth()
            .padding(leftRight = SidebarTheme.navItemPaddingX, topBottom = SidebarTheme.sectionLabelPaddingY)
            .fontSize(SidebarTheme.sectionLabelFontSize.cssRem)
            .fontWeight(FontWeight.SemiBold)
            .letterSpacing(SidebarTheme.sectionLabelLetterSpacing.em)
            .color(sitePalette.sectionLabel)
    )
}

@Composable
private fun NavItemLink(label: String, path: String, icon: String, isActive: Boolean) {
    val sitePalette = ColorMode.current.toSitePalette()
    val textColor = ColorMode.current.toPalette().color
    var isHovered by remember { mutableStateOf(false) }

    val bg = when {
        isActive -> sitePalette.selectedHighlight
        isHovered -> sitePalette.islandAccent
        else -> Color.argb(0x00000000)
    }

    Link(
        path,
        variant = UndecoratedLinkVariant,
        modifier = Modifier
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false }
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .padding(leftRight = SidebarTheme.navItemPaddingX, topBottom = SidebarTheme.navItemPaddingY)
                .borderRadius(SidebarTheme.navItemRadius)
                .gap(SidebarTheme.navItemGapInner)
                .backgroundColor(bg)
                .styleModifier {
                    property("transition", "background-color 0.15s ease")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Span(
                Modifier
                    .fontSize(SidebarTheme.navIconFontSize.cssRem)
                    .styleModifier {
                        property("width", "${SidebarTheme.navIconWidth}rem")
                        property("text-align", "center")
                    }
                    .then(
                        if (isActive) Modifier.color(sitePalette.accent)
                        else Modifier.color(sitePalette.sidebarIcon)
                    )
                    .toAttrs { attr("class", "fas $icon") }
            )
            SpanText(
                label,
                Modifier
                    .fontSize(SidebarTheme.navTextFontSize.cssRem)
                    .then(
                        if (isActive) Modifier.fontWeight(FontWeight.Bold).color(textColor)
                        else Modifier.color(sitePalette.sidebarText)
                    )
            )
        }
    }
}

@Composable
private fun NavExtLink(href: String, label: String, icon: String) {
    val sitePalette = ColorMode.current.toSitePalette()
    var isHovered by remember { mutableStateOf(false) }

    Link(
        href,
        variant = UndecoratedLinkVariant,
        modifier = Modifier
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false }
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .padding(leftRight = SidebarTheme.navItemPaddingX, topBottom = SidebarTheme.navItemPaddingY)
                .borderRadius(SidebarTheme.navItemRadius)
                .gap(SidebarTheme.navItemGapInner)
                .backgroundColor(if (isHovered) sitePalette.islandAccent else Color.argb(0x00000000))
                .styleModifier {
                    property("transition", "background-color 0.15s ease")
                }
                .color(sitePalette.sidebarText),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Span(
                Modifier
                    .fontSize(SidebarTheme.navIconFontSize.cssRem)
                    .styleModifier {
                        property("width", "${SidebarTheme.navIconWidth}rem")
                        property("text-align", "center")
                    }
                    .color(sitePalette.sidebarIcon)
                    .toAttrs { attr("class", "fas $icon") }
            )
            SpanText(
                label,
                Modifier
                    .fontSize(SidebarTheme.navTextFontSize.cssRem)
                    .color(sitePalette.sidebarText)
            )
            Box(Modifier.margin(left = 0.25.cssRem)) {
                Span(
                    Modifier
                        .fontSize(SidebarTheme.navExtLinkFontSize.cssRem)
                        .color(sitePalette.sidebarIcon)
                        .toAttrs { attr("class", "fas fa-arrow-up-right-from-square") }
                )
            }
        }
    }
}
